import { Autocomplete, Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { API_LABEL, type ApiKind, REGIONS, type Tier } from "../model";
import { PARTNER_STATUSES } from "../partnerStatus";
import type { MetricsFilters, PartnerMetricsRow } from "../metrics.types";

const API_OPTIONS: { value: MetricsFilters["api"]; label: string }[] = [
  { value: "all", label: "All APIs" },
  ...Object.entries(API_LABEL).map(([kind, label]) => ({ value: kind as ApiKind, label })),
];

/**
 * The other filters show "All Tiers"/"All Regions" as their actual selected
 * value (full-opacity text) — match that look for an Autocomplete's "All X"
 * placeholder too, rather than the browser's dimmed placeholder style.
 */
const AUTOCOMPLETE_SX = {
  "& .MuiInputBase-input::placeholder": { opacity: 1, color: "text.primary" },
};

interface MetricsFilterBarProps {
  filters: MetricsFilters;
  isDefault: boolean;
  /** Every row (unfiltered) — the Partner select lists all partners regardless of the other filters. */
  allRows: PartnerMetricsRow[];
  tiers: Tier[];
  onPartnerChange: (partnerId: string | "all") => void;
  onChange: <K extends Exclude<keyof MetricsFilters, "partnerId">>(
    key: K,
    value: MetricsFilters[K]
  ) => void;
  onReset: () => void;
}

/**
 * The top-level filter row for the Metrics Dashboard: Partner, Tier, Region,
 * API, Status, and Reset. A port of the POC's `metricsFilters` controls.
 *
 * Partner and Region are `Autocomplete` (type-ahead) rather than a plain
 * select — both are open-ended lists that grow with real data (unlike Tier/
 * API/Status, which are small, closed business enums that stay small
 * regardless of data source). This mirrors twosion-ui's own convention:
 * `CountrySelector`/`EventSourceMarketPicker` there are searchable
 * Autocompletes backed by a ~250-country list, never a plain select, while
 * our own `REGIONS` (6 values today) is POC-scale illustrative data, not a
 * realistic ceiling. The PRD's "sortable/searchable Partner Metrics table"
 * line and PM-FR-002's "Admin selects a partner" also read as a
 * search-to-select interaction, not a separate free-text row filter — a
 * standalone search box alongside the Partner picker would just be two
 * controls doing overlapping jobs. The POC's per-column numeric range
 * filters (ACV, utilization %, etc.) are POC-only richness with no PRD
 * backing and are deliberately not ported.
 */
export function MetricsFilterBar({
  filters,
  isDefault,
  allRows,
  tiers,
  onPartnerChange,
  onChange,
  onReset,
}: MetricsFilterBarProps) {
  const selectedPartner = allRows.find((row) => row.id === filters.partnerId) ?? null;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <Autocomplete
          size="small"
          options={allRows}
          getOptionLabel={(row) => row.partner.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPartner}
          onChange={(_event, value) => onPartnerChange(value?.id ?? "all")}
          sx={{ minWidth: 220, ...AUTOCOMPLETE_SX }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Partner"
              placeholder="All Partners"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />

        <TextField
          select
          size="small"
          label="Tier"
          value={filters.tierId}
          onChange={(e) => onChange("tierId", e.target.value as MetricsFilters["tierId"])}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Tiers</MenuItem>
          {tiers.map((tier) => (
            <MenuItem key={tier.id} value={tier.id}>
              {tier.name}
            </MenuItem>
          ))}
        </TextField>

        <Autocomplete
          size="small"
          options={REGIONS}
          value={filters.region === "all" ? null : filters.region}
          onChange={(_event, value) => onChange("region", value ?? "all")}
          sx={{ minWidth: 180, ...AUTOCOMPLETE_SX }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Region"
              placeholder="All Regions"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />

        <TextField
          select
          size="small"
          label="API"
          value={filters.api}
          onChange={(e) => onChange("api", e.target.value as MetricsFilters["api"])}
          sx={{ minWidth: 150 }}
        >
          {API_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(e) => onChange("status", e.target.value as MetricsFilters["status"])}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          {PARTNER_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>

        <Button onClick={onReset} disabled={isDefault} size="small">
          Reset
        </Button>
      </Stack>
    </Box>
  );
}
